/**
 * Sprint 20 B5 (DS.8-06): Smart Defaults Learning — Layout Preferences
 *
 * Tracks which Tier 4 fields users expand. After 10+ expansions over 2 weeks →
 * auto-promote to Tier 3. After 30 days with zero interaction → demote to Tier 4.
 * Settings page to see and override auto-promoted/demoted fields.
 * Real apiFetch. CSS vars only.
 */
import { useState, useEffect, useCallback } from "react";
import { apiFetch } from "@/lib/api-client";
import { Skeleton, SkeletonCard } from "@/components/skeleton";
import { Settings, ArrowUp, ArrowDown, RotateCcw, Eye, EyeOff, Sparkles } from "lucide-react";

interface FieldPreference {
  fieldKey: string;
  label: string;
  module: string;
  currentTier: 3 | 4;
  defaultTier: 3 | 4;
  expansionCount: number;
  lastExpandedAt: string | null;
  autoPromoted: boolean;
  autoDemoted: boolean;
  userOverride: boolean;
}

const TIER_LABEL = {
  3: { label: "Visible", color: "text-[var(--color-success)]", bg: "bg-[var(--color-success)]/10" },
  4: { label: "Hidden", color: "text-[var(--color-muted-foreground)]", bg: "bg-[var(--color-surface)]" },
} as const;

export default function LayoutPreferencesPage() {
  const [fields, setFields] = useState<FieldPreference[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [savingKey, setSavingKey] = useState<string | null>(null);
  const [filter, setFilter] = useState<"all" | "promoted" | "demoted" | "overridden">("all");

  const fetchPreferences = useCallback(async () => {
    try {
      const res = await apiFetch<{ data: FieldPreference[] }>("/settings/layout-preferences");
      setFields(res.data);
    } catch { setFields([]); }
    finally { setIsLoading(false); }
  }, []);

  useEffect(() => { fetchPreferences(); }, [fetchPreferences]);

  const [error, setError] = useState<string | null>(null);

  const handleOverride = async (fieldKey: string, newTier: 3 | 4) => {
    setSavingKey(fieldKey);
    setError(null);
    // Store previous state for revert on failure
    const prev = fields.find((f) => f.fieldKey === fieldKey);
    // Optimistic update
    setFields((fs) =>
      fs.map((f) =>
        f.fieldKey === fieldKey ? { ...f, currentTier: newTier, userOverride: true } : f,
      ),
    );
    try {
      await apiFetch(`/settings/layout-preferences/${encodeURIComponent(fieldKey)}`, {
        method: "PATCH",
        body: JSON.stringify({ tier: newTier, userOverride: true }),
      });
    } catch {
      // Revert optimistic update
      if (prev) {
        setFields((fs) =>
          fs.map((f) =>
            f.fieldKey === fieldKey ? { ...f, currentTier: prev.currentTier, userOverride: prev.userOverride } : f,
          ),
        );
      }
      setError("Failed to update preference. Please try again.");
    } finally { setSavingKey(null); }
  };

  const handleReset = async (fieldKey: string) => {
    setSavingKey(fieldKey);
    setError(null);
    const prev = fields.find((f) => f.fieldKey === fieldKey);
    setFields((fs) =>
      fs.map((f) =>
        f.fieldKey === fieldKey ? { ...f, currentTier: f.defaultTier, userOverride: false } : f,
      ),
    );
    try {
      await apiFetch(`/settings/layout-preferences/${encodeURIComponent(fieldKey)}/reset`, { method: "POST" });
    } catch {
      if (prev) {
        setFields((fs) =>
          fs.map((f) =>
            f.fieldKey === fieldKey ? { ...f, currentTier: prev.currentTier, userOverride: prev.userOverride } : f,
          ),
        );
      }
      setError("Failed to reset preference. Please try again.");
    } finally { setSavingKey(null); }
  };

  const filtered = fields.filter((f) => {
    if (filter === "promoted") return f.autoPromoted;
    if (filter === "demoted") return f.autoDemoted;
    if (filter === "overridden") return f.userOverride;
    return true;
  });

  const grouped = filtered.reduce<Record<string, FieldPreference[]>>((acc, f) => {
    if (!acc[f.module]) acc[f.module] = [];
    acc[f.module]!.push(f);
    return acc;
  }, {});

  const promotedCount = fields.filter((f) => f.autoPromoted).length;
  const demotedCount = fields.filter((f) => f.autoDemoted).length;

  if (isLoading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3, 4].map((i) => <SkeletonCard key={i} className="h-14" />)}
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-3xl">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Settings className="h-5 w-5 text-[var(--color-primary)]" />
          <div>
            <h1 className="text-2xl font-semibold text-[var(--color-foreground)]">Layout Preferences</h1>
            <p className="text-sm text-[var(--color-muted-foreground)]">
              Fields auto-promote and demote based on your usage patterns
            </p>
          </div>
        </div>
      </div>

      {/* AI learning info */}
      <div className="flex items-center gap-3 rounded-lg border border-[var(--color-ai,var(--color-primary))]/20 bg-[var(--color-ai,var(--color-primary))]/5 px-4 py-3">
        <Sparkles className="h-4 w-4 text-[var(--color-ai,var(--color-primary))] shrink-0" />
        <p className="text-xs text-[var(--color-ai,var(--color-primary))]">
          Smart defaults learn from your behavior: fields you expand 10+ times over 2 weeks get promoted to visible.
          Fields untouched for 30 days get demoted. You can override any change below.
        </p>
      </div>

      {error && (
        <div className="flex items-center justify-between rounded-lg border border-[var(--color-error)]/20 bg-[var(--color-error)]/5 px-4 py-2">
          <p className="text-xs text-[var(--color-error)]">{error}</p>
          <button type="button" onClick={() => setError(null)} className="text-xs text-[var(--color-error)] hover:underline">Dismiss</button>
        </div>
      )}

      {/* Stats + Filter */}
      <div className="flex items-center justify-between">
        <div className="flex gap-4 text-xs text-[var(--color-muted-foreground)]">
          <span>{fields.length} total fields</span>
          <span className="text-[var(--color-success)]">{promotedCount} auto-promoted</span>
          <span>{demotedCount} auto-demoted</span>
        </div>
        <div className="flex rounded-lg bg-[var(--color-surface)] p-0.5">
          {(["all", "promoted", "demoted", "overridden"] as const).map((f) => (
            <button
              key={f}
              type="button"
              onClick={() => setFilter(f)}
              className={`rounded-md px-3 py-1 text-xs font-medium capitalize ${
                filter === f
                  ? "bg-[var(--color-background)] text-[var(--color-foreground)] shadow-sm"
                  : "text-[var(--color-muted-foreground)]"
              }`}
            >
              {f}
            </button>
          ))}
        </div>
      </div>

      {/* Field list grouped by module */}
      {Object.keys(grouped).length === 0 ? (
        <div className="rounded-xl border border-dashed border-[var(--color-border)] bg-[var(--color-surface)] p-12 text-center">
          <Settings className="mx-auto h-10 w-10 text-[var(--color-muted-foreground)] mb-3" />
          <p className="text-sm text-[var(--color-muted-foreground)]">No field preferences to show</p>
        </div>
      ) : (
        Object.entries(grouped).map(([module, moduleFields]) => (
          <div key={module} className="space-y-2">
            <h3 className="text-xs font-semibold uppercase text-[var(--color-muted-foreground)] px-1">
              {module}
            </h3>
            {moduleFields.map((field) => {
              const tier = TIER_LABEL[field.currentTier];
              const isSaving = savingKey === field.fieldKey;

              return (
                <div
                  key={field.fieldKey}
                  className="stagger-item flex items-center justify-between rounded-xl border border-[var(--color-border)] bg-[var(--color-background)] px-4 py-3"
                >
                  <div className="flex items-center gap-3">
                    {field.currentTier === 3
                      ? <Eye className="h-4 w-4 text-[var(--color-success)]" />
                      : <EyeOff className="h-4 w-4 text-[var(--color-muted-foreground)]" />}
                    <div>
                      <p className="text-sm font-medium text-[var(--color-foreground)]">{field.label}</p>
                      <div className="flex items-center gap-2 text-[10px] text-[var(--color-muted-foreground)]">
                        <span>{field.expansionCount} expansions</span>
                        {field.lastExpandedAt && (
                          <span>| last: {new Date(field.lastExpandedAt).toLocaleDateString()}</span>
                        )}
                        {field.autoPromoted && (
                          <span className="rounded-full bg-[var(--color-success)]/10 px-1.5 py-0.5 text-[var(--color-success)]">
                            auto-promoted
                          </span>
                        )}
                        {field.autoDemoted && (
                          <span className="rounded-full bg-[var(--color-warning)]/10 px-1.5 py-0.5 text-[var(--color-warning)]">
                            auto-demoted
                          </span>
                        )}
                        {field.userOverride && (
                          <span className="rounded-full bg-[var(--color-primary)]/10 px-1.5 py-0.5 text-[var(--color-primary)]">
                            manual override
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <span className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${tier.bg} ${tier.color}`}>
                      Tier {field.currentTier}: {tier.label}
                    </span>

                    {isSaving ? (
                      <Skeleton className="h-4 w-4 rounded-full bg-[var(--color-muted-foreground)]/20" />
                    ) : (
                      <div className="flex gap-1">
                        {field.currentTier === 4 ? (
                          <button
                            type="button"
                            onClick={() => handleOverride(field.fieldKey, 3)}
                            title="Promote to visible"
                            className="rounded p-1 text-[var(--color-muted-foreground)] hover:bg-[var(--color-success)]/10 hover:text-[var(--color-success)]"
                          >
                            <ArrowUp className="h-3.5 w-3.5" />
                          </button>
                        ) : (
                          <button
                            type="button"
                            onClick={() => handleOverride(field.fieldKey, 4)}
                            title="Demote to hidden"
                            className="rounded p-1 text-[var(--color-muted-foreground)] hover:bg-[var(--color-warning)]/10 hover:text-[var(--color-warning)]"
                          >
                            <ArrowDown className="h-3.5 w-3.5" />
                          </button>
                        )}
                        {field.userOverride && (
                          <button
                            type="button"
                            onClick={() => handleReset(field.fieldKey)}
                            title="Reset to smart default"
                            className="rounded p-1 text-[var(--color-muted-foreground)] hover:bg-[var(--color-surface)]"
                          >
                            <RotateCcw className="h-3.5 w-3.5" />
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        ))
      )}
    </div>
  );
}
